import React from 'react'
import { Grid, Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

const useStyles = makeStyles<Theme, MissingRendererPlaceholderProps>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(6, 3),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.borderRadius.lg
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center'
    },
    type: {
      fontFamily: 'monospace'
    }
  }),
  { name: 'MissingRendererPlaceholder' }
)

export interface MissingRendererPlaceholderProps {
  /** The block's `type`, shown verbatim so the author can report it. */
  type: string
  isPreview?: boolean
  isSettings?: boolean
  classes?: StyleClasses<typeof useStyles>
}

/**
 * Stands in for a block whose `type` has no v2 renderer — the v1-only `cover`, `introduction`,
 * `socials`, `sliderImage` and `benjamin-moore-main`, or `video`, which is in the catalog but was
 * never built. Before #22775 such a block was dropped from the render with no warning anywhere, so
 * an author had a block they could neither see nor delete.
 *
 * Editable canvas only. Customer view, print and preview drop the block before it reaches here
 * (`getVisibleBlocks`); the guard below is a second latch so a future caller cannot leak an
 * internal notice to a customer.
 */
function MissingRendererPlaceholder(props: MissingRendererPlaceholderProps) {
  const classes = useStyles(props)
  const { type, isPreview, isSettings } = props

  if (!isSettings || isPreview) {
    return null
  }

  return (
    <div className={classes.root} data-testid={'presentation-block-missing-renderer'} data-block-type={type}>
      <Grid className={classes.container} container spacing={1}>
        <Grid item xs={12}>
          <Typography variant={'h4'}>{`This block can't be shown`}</Typography>
        </Grid>
        <Grid className={classes.container} item xs={12}>
          <Typography variant={'subtitle1'}>
            {`There is no editor for the "`}
            <span className={classes.type}>{type}</span>
            {`" block type, so it stays hidden from customers. Delete it, or replace it with a block from the Add panel.`}
          </Typography>
        </Grid>
      </Grid>
    </div>
  )
}

export default MissingRendererPlaceholder
